// List of events to be tracked
// Event Naming : capitalised variable name in snake_case, camelCase for values

import { SmartPickingIssueType } from "__graphql__/types";
import { EventBase } from "analytics/types";
import { Check } from "flows/Inventory/shared/models/check/types";
import { MapDiscriminatedUnion } from "utils/tsTypesHelpers";

/**
 *
 *
 * ========== Page events ============
 *
 *
 */

// page names
export enum PageName {
  ACTIVITIES_PAGE = "activities",
  LAYOUT = "layout",
  HUB_AUTHENTICATION_PAGE = "hub_authentication",
  USER_IDENTIFICATION_PAGE = "user_identification",
  NEW_ORDER_FOR_PICKING_PAGE = "new_order_for_picking",
  PICKING_PAGE = "picking",
  PICKING_INCOMPLETE_PAGE = "picking_incomplete",
  MANUAL_PICKING_PAGE = "manual_picking",
  INBOUND_SELECT_DELIVERY = "select_delivery",
  INBOUND = "inbounding",
  INBOUND_PAGE = "list_preparation",
  INBOUND_DROPPING_PAGE = "dropping_list",
  INBOUND_PAGE_SHARED_LIST = "inbound/shared-list",
  INBOUND_PAGE_LIST_VERIFICATION = "inbound/list-verification",
  INVENTORY_PAGE = "inventory",
  INVENTORY_PAGE_PRIVATE_LIST = "inventory/restocking-list",
  INVENTORY_PAGE_PUBLIC_LIST = "inventory/restocking-list/public-list",
  INVENTORY_PAGE_MOVE_ITEMS = "inventory/restocking-list/move-items",
  INVENTORY_PAGE_DROP_OFF = "inventory/restocking-list/drop-off",
  INVENTORY_FRESH_CHECK_LIST_PAGE = "inventory/fresh-check-list",
  INVENTORY_BBD_CHECK_LIST_PAGE = "inventory/bbd-check-list",
  INVENTORY_STOCK_CHECK_LIST_PAGE = "inventory/stock-check-list",
  INVENTORY_ROLLING_INVENTORY_CHECK_LIST_PAGE = "inventory/rolling-inventory-checks",
  INVENTORY_SHELF_CHANGES_PAGE = "inventory/shelf-changes",
  INVENTORY_BATTERY_MANAGEMENT_PAGE = "inventory/battery-management",
  INVENTORY_BATTERY_COUNT_PAGE = "inventory/battery-count",
  INVENTORY_BBD_DECLARE_EXPIRED = "outbound_product_bbd",
  INVENTORY_BBD_DECLARE_CLOSEST_BBD = "expiration_date_bbd",
  INVENTORY_EOY_CHECK_LIST_PAGE = "inventory/eoy-checks",
  INVENTORY_RECOUNT_CHECK_LIST_PAGE = "inventory/recount-check",
  INVENTORY_INBOUND_DEVIATION_CHECK_LIST_PAGE = "inventory/inbound-deviation-check-list",
  TASK_PAGE = "task",
  STOCK_CHANGE_PAGE = "stock_change",
  BBD_CORRECTION_THIS_DATE_CANT_BE_EDITED = "this_date_cant_be_edited",
  HUB_ACTIVITIES = "hub_activities",
  DELIVERY_CHECK_IN = "delivery_check_in",
  STOCK_CHECK = "stock_check",
  BAG_PREPARATION = "bag_preparation",
  TRANSPORT_EQUIPMENT_CONFIRMATION = "transport_equipment_confirmation",
  TRANSPORT_EQUIPMENT_COUNT = "transport_equipment_count",
  TRANSPORT_EQUIPMENT_PRECOUNT = "transport_equipment_precount",
}

/**
 *
 *
 * ========== Login events ============
 *
 *
 */

type AuthActionType = "staff" | "hub";
export type AuthLogoutActionMethod = "manual" | "automatic";
export type IdentifierMethodType = "qr_scan" | "manual";

/**
 *
 *
 * ========== Track events ============
 *
 *
 */

export interface TrackEventBase extends EventBase {
  name: string;
}

/**
 * Sent whenever the application is opened.
 *
 * @type ApplicationOpenedEventType
 */
export interface ApplicationOpenedEventType extends TrackEventBase {
  name: "applicationOpened";
}

/**
 * Sent whenever the application is backgrounded.
 *
 * @type ApplicationBackgroundedEventType
 */
export interface ApplicationBackgroundedEventType extends TrackEventBase {
  name: "applicationBackgrounded";
}

/**
 * Sent whenever an error is shown.
 *
 * @type ErrorShownEventType
 * @property {string} screen_name
 * @property {string} component_value
 */
export interface ErrorShownEventType extends TrackEventBase {
  name: "errorShown";
  payload: {
    screen_name: PageName;
    component_value: string;
    component_content?: string;
    component_name?: string;
    component_variant?: string;
  };
}

/**
 * Sent whenever a login is successful.
 *
 * @type LoginCompletedEventType
 * @property {string} type
 * @property {string} user_id
 * @property {string} hub_slug
 */
export interface LoginCompletedEventType extends TrackEventBase {
  name: "loginCompleted";
  payload: {
    type: AuthActionType;
    hub_slug: string;
    quinyx_badge_number?: string;
    employee_id?: string;
    identifier_method?: IdentifierMethodType;
  };
}

/**
 * Sent whenever a logout is successful.
 *
 * @type LogoutCompletedEventType
 * @property {string} type
 * @property {string} user_id
 * @property {string} hub_slug
 */
export interface LogoutCompletedEventType extends TrackEventBase {
  name: "logoutCompleted";
  payload: {
    type: AuthActionType;
    hub_slug: string;
    quinyx_badge_number?: string;
    employee_id?: string;
    method: AuthLogoutActionMethod;
  };
}

/**
 * Sent whenever a button is clicked.
 *
 * @type InAppMessageShownEventType
 * @property {string} screen_name
 * @property {string} component_name
 * @property {string} component_value
 */
export interface InAppMessageShownEventType extends TrackEventBase {
  name: "inAppMessageShown";
  payload: {
    screen_name: PageName;
    component_name?: string;
    component_content?: string;
  };
}

/**
 * Sent whenever a button is clicked.
 *
 * @type ClickEventType
 * @property {string} screen_name
 * @property {string} component_name
 * @property {string} component_value
 */
export interface ClickEventType extends TrackEventBase {
  name: "click";
  payload: {
    screen_name: PageName;
    component_name?: string | GuardrailMap;
    component_value?: string;
    component_content?: string;
    component_variation?: string;
  };
}

export enum GuardrailMap {
  QuantityExceeded = "unusual_quantity_guardrail",
  ValueExceeded = "unusual_value_guardrail",
}

export interface GuardrailProgressedEventType extends TrackEventBase {
  name: "guardrail_progressed";
  payload: {
    action: "confirm" | "cancel";
    process_id?: string;
    product_sku?: string;
    ean?: string;
    attempted_quantity?: number;
    screen_name?: PageName;
    guardrail_type?: string | GuardrailMap;
  };
}
export interface OrderProgressedEventType extends TrackEventBase {
  name: "orderProgressed";
  payload: {
    action: string;
    product_sku: string | null;
    ean: (string | null)[] | null;
    order_number: string;
    order_id: string;
    quantity: number;
    method?: string;
    reason?: string | null;
    scanned_barcode: string | null;
    product_pricing_type: "weight_based" | "fixed" | null;
    alert_shown: boolean;
    alert_issue_type: SmartPickingIssueType | null;
  };
}

export interface OrderStateUpdatedEventType extends TrackEventBase {
  name: "orderStateUpdated";
  payload: {
    state: "offered" | "picked" | "picking_finished" | "packed";
    origin: "PICKING_SCREEN" | "QR_CODE" | null;
    order_number?: string | null;
    order_id?: string;
  };
}

export interface ContainerAssignedEventType extends TrackEventBase {
  name: "containerAssigned";
  payload: {
    type: "Container" | "Shelf";
    method: "scanned" | "manual";
    container_ids: (string | null)[];
    order_number?: string | null;
    order_id?: string;
  };
}

export interface ContainerAssignmentSkippedEventType extends TrackEventBase {
  name: "containerAssignmentSkipped";
  payload: {
    type: "Container" | "Shelf";
    order_number?: string | null;
    order_id?: string;
  };
}

export interface StockCheckStartedEventType extends TrackEventBase {
  name: "stockCheckStarted";
  payload: {
    /**
     * If the check performed is an automatically-generated check then type = automated
     * If the check performed is a manual check (from stock correction flow) then type = manual
     */
    type: "automated" | "manual";
    check_id: Check["checkId"];
    product_sku: Check["sku"];
    origin: PageName;
  };
}

export interface StockCheckFinishedEventType extends TrackEventBase {
  name: "stockCheckFinished";
  payload: {
    /**
     * If the check performed is an automatically-generated check then type = automated
     * If the check performed is a manual check (from stock correction flow) then type = manual
     */
    type: "automated" | "manual";
    check_id: Check["checkId"];
    product_sku: Check["sku"];
    quantity_expected: number;
    quantity_counted: number;
    quantity_expired: number;
    quantity_damaged: number;
    quantity_tgtg: number;
    next_expiration_date?: string;
  };
}

// ===== Collaborative Inbounding =====

export enum CollaborativeInboundUpdateState {
  ListPreparationStarted = "list_preparation_started",
  ListPreparationFinished = "list_preparation_finished",
  ListPreparationPaused = "list_preparation_paused",
  ListPreparationResumed = "list_preparation_resumed",
  DroppingListStarted = "dropping_list_started",
  DroppingListFinished = "dropping_list_finished",
  DroppingListUnclaimed = "dropping_list_unclaimed",
  DroppingListPaused = "dropping_list_paused",
  DroppingListResumed = "dropping_list_resumed",
  DroppingListDeleted = "dropping_list_deleted",
  ListVerificationStarted = "list_verification_started",
}
export enum CollaborativeInboundProgressedAction {
  ProductAddedToList = "product_added_to_list",
  ProductUpdatedQuantity = "product_updated_quantity",
  ProductRemovedFromList = "product_removed_from_list",
  ProductDropped = "product_dropped",
}

export enum CollaborativeInboundProgressedMethod {
  Scanned = "scanned",
  TextSearched = "text_searched",
  AdvancedSearched = "advanced_searched",
  ManualSU = "manual_su",
  ManualHU = "manual_hu",
}

export enum CollaborativeInboundStage {
  ListPreparation = "list_preparation",
  ListDropping = "list_dropping",
}

export enum CollaborativeListPreparationMethod {
  Automatic = "automatic",
  Manual = "manual",
}

export enum OutboundOrderCancellationReason {
  NotReturned = "not_returned",
  NotSellable = "not_sellable",
}
export interface InboundStateUpdated extends TrackEventBase {
  name: "inboundStateUpdated";
  payload: {
    state: CollaborativeInboundUpdateState;
    dropping_list_id: string | null;
    sscc: string | null;
    inbounding_type: string | null;
    is_manual_unclaim?: boolean;
    list_preparation_method?: CollaborativeListPreparationMethod;
    order_id?: string | null;
  };
}
export interface InboundProgressed extends TrackEventBase {
  name: "inboundProgressed";
  payload: {
    dropping_list_id: string;
    product_sku: string;
    action: CollaborativeInboundProgressedAction;
    is_handling_unit?: boolean;
    method?: CollaborativeInboundProgressedMethod;
    quantity: number;
    sscc?: string | null;
    shelf_number?: string | null;
    temporary_handling_unit_size?: number;
    dropping_list_name?: string | null;
    inbounding_stage?: CollaborativeInboundStage;
    order_id?: string | null;
    reason?: OutboundOrderCancellationReason | null;
  };
}

export type StockCorrectionsOrigin = "scanned_product" | "scanned_shelf" | "search_bar";
export type StockCorrectionsScreenName =
  "stock_correction_multiple_results" | "stock_correction_screen";
export interface StockCorrectionsStarted extends TrackEventBase {
  name: "stockCorrectionStarted";
  payload: {
    stock_correction_id: string;
    product_sku: string;
    shelf_number: string;
    origin: StockCorrectionsOrigin;
    screen_name: StockCorrectionsScreenName;
    shelf_change_id?: string | null; // Linked shelf change proposal ID (if triggered from shelf change flow)
  };
}

export interface StockCorrectionsFinished extends TrackEventBase {
  name: "stockCorrectionFinished";
  payload: {
    stock_correction_id: string;
    screen_name: StockCorrectionsScreenName;
    product_sku: string;
    shelf_number: string;
    quantity_before_correction: number;
    quantity_after_correction: number;
    reason?: string;
    quantity?: number;
    shelf_change_id?: string | null; // Linked shelf change proposal ID (if triggered from shelf change flow)
  };
}

export type OutboundProgressedAction = "product_added_to_outbound" | "product_outbounded";

export interface OutboundProgressed extends TrackEventBase {
  name: "outboundProgressed";
  payload: {
    dropping_list_id: string;
    product_sku: string;
    action: OutboundProgressedAction;
    quantity_expired: number;
    quantity_damaged: number;
    quantity_perished: number;
    quantity_received: number;
    sscc: string | null;
    shelf_number: string | null;
  };
}

export type InboundVerificationReason =
  | "quantity_equal_to_one"
  | "quantity_more_than_fifty"
  | "quantity_handling_unit_mismatch"
  | "quantity_above_expected_by_50_percent";

export interface InboundVerificationProgressed extends TrackEventBase {
  name: "inboundVerificationProgressed";
  payload: {
    dropping_list_id: string;
    sscc: string | null;
    product_sku: string;
    verification_reason: InboundVerificationReason;
    quantity_before_verification: number;
    quantity_after_verification: number;
    running_number: number;
  };
}

// ===== Bbd correction events =====
export type BbdDateCorrectionOrigin = "scanned_product" | "search_bar";
export interface BbdDateCorrectionStartedEventType extends TrackEventBase {
  name: "bbdDateCorrectionStarted";
  payload: {
    product_sku: string;
    shelf_number: string;
    origin: BbdDateCorrectionOrigin;
  };
}

export interface BbdDateCorrectionFinishedEventType extends TrackEventBase {
  name: "bbdDateCorrectionFinished";
  payload: {
    product_sku: string;
    next_expiration_date_before: string;
    next_expiration_date_after: string;
  };
}

export interface HubActivityStarted extends TrackEventBase {
  name: "hubActivityStarted";
  payload: {
    screen_name: PageName.HUB_ACTIVITIES;
    category_type: string;
    hub_activity_id: string;
  };
}

type HubActivityFinishedOrigin = "origin_activity_popup" | "activity_flow";
export interface HubActivityFinished extends TrackEventBase {
  name: "hubActivityFinished";
  payload: {
    screen_name: PageName;
    origin: HubActivityFinishedOrigin;
    category_type: string;
    hub_activity_id: string;
    task_completion_time_seconds: number;
  };
}

type RestockingState =
  "restocking_list_preparation_started" | "restocking_list_started" | "restocking_list_finished";

export interface RestockingStateUpdated extends TrackEventBase {
  name: "restockingStateUpdated";
  payload: {
    restocking_list_id: string;
    state: RestockingState;
  };
}

type RestockingProgressedAction =
  | "product_added_to_list"
  | "product_updated_quantity"
  | "product_removed_from_list"
  | "product_restocked";

export const RESTOCKING_PROGRESSED_METHOD = {
  SCANNED_PRODUCT: "scanned_product",
  TEXT_SEARCHED: "text_searched",
  SCANNED_SHELF: "scanned_shelf",
  PRE_LOADED_LIST: "pre_loaded_list",
} as const;

export type RestockingProgressedMethod =
  (typeof RESTOCKING_PROGRESSED_METHOD)[keyof typeof RESTOCKING_PROGRESSED_METHOD];

export interface RestockingProgressed extends TrackEventBase {
  name: "restockingProgressed";
  payload: {
    restocking_list_id: string;
    action: RestockingProgressedAction;
    method?: RestockingProgressedMethod;
    product_sku: string;
    shelf_number?: string;
    quantity?: number;
  };
}

// ===== TGTG Events =====
export enum TGTGBagUpdateStates {
  BagPreparationStarted = "bag_preparation_started", // State when bag preparation begins
  BagPreparationFinished = "bag_preparation_finished", // State when bag preparation is complete
}

export enum TGTGBagPreparationFeatures {
  TgtgAssistant = "tgtg_assistant",
}

export enum TGTGBagProgressAction {
  ProductAdded = "product_added", // When a user adds a product to the bag
  ProductRemoved = "product_removed", // When a user removes a product from the bag
  ProductQuantityUpdated = "product_quantity_updated", // When a user updates the quantity of a product in the bag
}

export enum TGTGBagAdditionMethod {
  Scanned = "scanned", // User scanned the QR code
  Manual = "manual", // User manually inputted the product's information
  TextSearched = "text_searched", // User searched and selected the product
  NoEANSearched = "advanced_searched", // User searched and selected the product by category
}
export interface BagStateUpdated extends TrackEventBase {
  name: "bagStateUpdated";
  payload: {
    state: TGTGBagUpdateStates; // Current state of the bag
    bag_id: string; // Unique identifier of the bag
    bag_value_final: number | null; // Actual monetary value of the bag (null if preparation just started)
    bag_value_target: number; // Expected monetary value of the bag
    bag_type: string; // Type of bag selected by the user
    bag_value_limit: number; // Maximum allowed value for the bag
    bag_preparation_feature: TGTGBagPreparationFeatures; // Feature used for bag preparation (e.g., "tgtg_assistant")
  };
}

export interface BagProgressed extends TrackEventBase {
  name: "bagProgressed";
  payload: {
    action: TGTGBagProgressAction; // Event triggers
    bag_id: string; // Unique identifier of the bag
    product_sku: string | null; // SKU of the product
    ean: string; // EAN of the product
    quantity: number; // Quantity of the product
    method: TGTGBagAdditionMethod | null; // The method used to add a product (only for 'product_added')
    product_name: string; // Name of the product added
    product_price: number; // Price of one unit of the product
  };
}

// ===== Shelf Change Events =====
export enum ShelfChangeProgressState {
  Start = "start_shelf_change", // When the old shelf is scanned
  Finish = "finish_shelf_change", // When the new shelf is scanned or finish button clicked
  AdjustStock = "adjust_stock_shelf_change", // When the adjust stock button is clicked
}

export enum ShelfChangeAction {
  Scan = "scan", // Triggered by scanning QR code
  Click = "click", // Triggered by clicking a button
}
export interface ShelfChangeStateUpdatedEventType extends TrackEventBase {
  name: "shelfChangeStateUpdated";
  payload: {
    state: ShelfChangeProgressState;
    action: ShelfChangeAction;
    shelf_change_id: string; // proposal_id
    product_sku?: string;
    product_name?: string;
    from_shelf_id?: string;
    to_shelf_id?: string;
  };
}

// ===== Asset Count Events =====
export enum AssetCountEventType {
  AssetCountStarted = "asset_count_started", // When the asset count flow is started
  AssetCountFinished = "asset_count_finished", // When the asset count flow is finished
}

export enum AssetCountStatus {
  Pending = "Pending", // Asset count process pending
  Done = "Done", // Asset count process completed
}

export interface AssetCountStateUpdatedEventType extends TrackEventBase {
  name: "assetCountStateUpdated";
  payload: {
    asset_event_type: AssetCountEventType; // Indicates whether the count was started or finished
    asset_count_process_id: string; // Unique identifier of the asset count task
    asset_count_session_id: string; // Unique identifier for this specific count session
    status: AssetCountStatus; // Current status of the count process
    asset_type: string; // Category of the asset type (e.g., "BIKE_BATTERY")
    number_of_asset_counted: number | null; // Total number of assets scanned so far in this session
  };
}

export interface AssetMissingConfirmedEventType extends TrackEventBase {
  name: "assetMissingConfirmed";
  payload: {
    asset_type: string; // Category of the asset type (e.g., "BIKE_BATTERY")
    asset_count_process_id: string; // Unique identifier of the asset count process
    asset_count_session_id: string; // Unique identifier for this specific count session
    expected_asset_count: number; // Quantity of assets expected in the system
    scanned_asset_count: number; // Quantity of assets physically scanned by OAs
  };
}

export interface AssetOutboundConfirmedEventType extends TrackEventBase {
  name: "assetOutboundConfirmed";
  payload: {
    asset_type: string; // Category of the asset type (e.g., "BIKE_BATTERY")
    asset_count_process_id: string; // Unique identifier of the asset count process
    asset_count_session_id: string; // Unique identifier for this specific count session
    number_of_outbounded_assets: number; // Total number of assets being outbounded
    assets: {
      asset_id: string; // Unique identifier of the asset
      asset_state: string; // State of the asset (e.g., "outbound")
      reason: string; // Reason for outbound: Lost, Repair, Stolen, or Transfer
    }[];
  };
}

export enum AssetInboundedAction {
  Create = "create",
  Transfer = "transfer",
  UpdateState = "update_state",
}

export interface AssetInboundedEventType extends TrackEventBase {
  name: "assetInbounded";
  payload: {
    asset_type: string; // Category of the asset type (e.g., "BIKE_BATTERY")
    asset_count_process_id: string; // Unique identifier of the asset count process
    asset_count_session_id: string; // Unique identifier for this specific count session
    flow_type: string; // Flow where the inbound happened (e.g., "battery_count")
    asset_id: string; // Unique identifier of the asset
    supplier: string; // Supplier name
    model: string; // Model name
    previous_state: string | null; // Previous state of the asset before inbound (null for newly created assets)
    action: AssetInboundedAction; // Type of inbound action performed
  };
}

export interface AssetDamageReportedEventType extends TrackEventBase {
  name: "assetDamagedReported";
  payload: {
    asset_type: string; // Category of the asset type (e.g., "BIKE_BATTERY")
    asset_count_process_id: string; // Unique identifier of the asset count process
    asset_count_session_id: string; // Unique identifier for this specific count session
    flow_type: string; // Flow where the damage was reported (e.g., "battery_count")
    asset_id: string; // Unique identifier of the asset
    asset_state: string; // State of the asset after being reported as damaged (e.g., "defect")
    reason: string; // Reason for reporting the damage (e.g., ‘Not working/not charging’, ‘Low capacity/drains very quickly’, etc.)
  };
}

export type TrackEventTypes =
  | ApplicationOpenedEventType
  | ApplicationBackgroundedEventType
  | LoginCompletedEventType
  | LogoutCompletedEventType
  | ClickEventType
  | OrderProgressedEventType
  | OrderStateUpdatedEventType
  | ErrorShownEventType
  | ContainerAssignedEventType
  | ContainerAssignmentSkippedEventType
  | StockCheckStartedEventType
  | StockCheckFinishedEventType
  | InAppMessageShownEventType
  | StockCorrectionsStarted
  | StockCorrectionsFinished
  | InboundStateUpdated
  | InboundProgressed
  | OutboundProgressed
  | InboundVerificationProgressed
  | BbdDateCorrectionStartedEventType
  | BbdDateCorrectionFinishedEventType
  | HubActivityStarted
  | HubActivityFinished
  | RestockingStateUpdated
  | RestockingProgressed
  | BagStateUpdated
  | BagProgressed
  | ShelfChangeStateUpdatedEventType
  | AssetCountStateUpdatedEventType
  | AssetMissingConfirmedEventType
  | AssetOutboundConfirmedEventType
  | AssetInboundedEventType
  | GuardrailProgressedEventType
  | AssetDamageReportedEventType;

export type TrackEventTypesMap = MapDiscriminatedUnion<TrackEventTypes, "name">;
